/**
 * PATH: apps/quote/src/lib/api/snapshots-api.ts
 * FILE_ID: QB-API-SNAPSHOTS-001
 * PURPOSE: Thin REST client for QuoteSnapshot endpoints.
 * WHY: Keeps the snapshot endpoints alongside the rest of the
 *      decomposed API modules instead of bloating quoteApi.ts.
 */

import { getApiClient, withOrgConfig } from '../quote-api-helpers'

export type SnapshotSource = 'manual' | 'auto_save' | 'pre_bulk' | 'pre_send'

export interface QuoteSnapshotTotals {
  chargeout_total?: number
  cost_total?: number
  margin_amount?: number
  margin_percent?: number
  line_count?: number
  section_count?: number
}

export interface QuoteSnapshotListItem {
  id: string
  quote: string | null
  quote_number: string
  revision: number
  source: SnapshotSource
  label: string
  note: string
  totals: QuoteSnapshotTotals
  created_at: string
  created_by: number | null
  created_by_name: string
}

export interface QuoteSnapshotDetail extends QuoteSnapshotListItem {
  // Full QuoteDetailSerializer payload at capture time. Schema may
  // drift over time so consumers must tolerate missing fields.
  data: Record<string, unknown>
}

export interface CreateSnapshotRequest {
  label?: string
  note?: string
  source?: SnapshotSource
}

export const snapshotsApi = {
  async list(quoteId: string): Promise<QuoteSnapshotListItem[]> {
    const client = getApiClient()
    const res = await client.get<QuoteSnapshotListItem[]>(
      `/quotebuilder/quotes/${quoteId}/snapshots/`,
      withOrgConfig(),
    )
    return res.data ?? []
  },

  async get(quoteId: string, snapId: string): Promise<QuoteSnapshotDetail> {
    const client = getApiClient()
    const res = await client.get<QuoteSnapshotDetail>(
      `/quotebuilder/quotes/${quoteId}/snapshots/${snapId}/`,
      withOrgConfig(),
    )
    return res.data
  },

  async create(
    quoteId: string,
    body: CreateSnapshotRequest = {},
  ): Promise<QuoteSnapshotDetail> {
    const client = getApiClient()
    const res = await client.post<QuoteSnapshotDetail>(
      `/quotebuilder/quotes/${quoteId}/snapshots/`,
      body,
      withOrgConfig(),
    )
    return res.data
  },

  async remove(quoteId: string, snapId: string): Promise<void> {
    const client = getApiClient()
    await client.delete(
      `/quotebuilder/quotes/${quoteId}/snapshots/${snapId}/`,
      withOrgConfig(),
    )
  },
}
